import Swiper from 'swiper';
import {
    Navigation, Pagination, A11y, EffectFade, FreeMode
}             from 'swiper/modules';

/** @type {number} Default breakpoint in pixels */
const DEFAULT_BREAKPOINT = 681;

/** @type {Object} Default accessibility labels */
const DEFAULT_A11Y = {
    prevSlideMessage        : 'Previous slide',
    nextSlideMessage        : 'Next slide',
    paginationBulletMessage : 'Go to slide {{index}}',
    firstSlideMessage       : 'First slide'
};

/**
 * Create a responsive slider that initializes/destroys based on breakpoint
 *
 * @param {HTMLElement} el - The element that contains the slider
 * @param {Object} config - Configuration options
 * @param {number} [config.breakpoint=681] - Breakpoint in pixels (slider disabled above this width)
 * @param {Object} [config.a11y] - Accessibility labels (merged with defaults)
 * @param {Object} [config.settings={}] - Swiper settings to merge with defaults
 * @returns {{swiper: function(): Swiper, cleanup: function(): void}|false}
 */
function createResponsiveSlider(el, config = {}) {
    if (!el) {
        return false;
    }

    const slider     = el.querySelector('.swiper');
    const pagination = el.querySelector('.swiper-pagination');

    if (!slider) {
        console.warn('Slider element (.swiper) not found');
        return false;
    }

    const breakpointValue = config.breakpoint ?? DEFAULT_BREAKPOINT;
    const {
        a11y    :a11yConfig,
        settings:userSettings = {},
        ...restConfig
    }               = config;

    // Remove processed keys so they don't leak into Swiper settings
    delete restConfig.breakpoint;

    const breakpoint = window.matchMedia(`(min-width: ${breakpointValue}px)`);

    let swiper;
    let mediaQueryListener;

    const settings = {
        modules               : [Navigation, Pagination, A11y, EffectFade, FreeMode],
        loop                  : false,
        slidesPerView         : 'auto',
        preloadImages         : false,
        watchSlidesVisibility : true,
        watchOverflow         : true,
        threshold             : 0,
        spaceBetween          : 12,
        observer              : true,
        a11y                  : {
            ...DEFAULT_A11Y,
            ...a11yConfig
        },
        pagination : {
            el             : pagination,
            type           : 'bullets',
            clickable      : true,
            dynamicBullets : false
        },
        ...userSettings
    };

    /** Initialize the Swiper slider */
    const enableSlider = () => {
        if (!swiper) {
            swiper = new Swiper(slider, settings);
        }
    };

    /** Destroy the Swiper slider */
    const disableSlider = () => {
        if (swiper) {
            swiper.destroy(true, true);
            swiper = undefined;
        }
    };

    /** Check breakpoint and enable/disable slider accordingly */
    const breakpointChecker = () => {
        if (breakpoint.matches) {
            disableSlider();
        } else {
            enableSlider();
        }
    };

    /** Cleanup function to remove event listeners */
    const cleanup = () => {
        disableSlider();
        if (mediaQueryListener) {
            try {
                breakpoint.removeEventListener('change', mediaQueryListener);
            } catch (e) {
                // Safari <14
                breakpoint.removeListener(mediaQueryListener);
            }
        }
    };

    mediaQueryListener = breakpointChecker;
    try {
        breakpoint.addEventListener('change', mediaQueryListener);
    } catch (e) {
        // Safari <14
        breakpoint.addListener(mediaQueryListener);
    }

    breakpointChecker();

    return {
        swiper : () => swiper,
        cleanup
    };
}

export { createResponsiveSlider };
